import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    collection,
    onSnapshot,
    query,
    where
} from "firebase/firestore";
import { db } from "../../firebase";
import { THEME_COLOR } from "../../config/club";
export default function GlobalAdsModal({
    clubId,
    themeColor = THEME_COLOR,
    sessionKey = "default"
}) {

    const [ads, setAds] = useState([]);
    const [show, setShow] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [loadedImages, setLoadedImages] = useState({});
    const touchStartX = useRef(0);

    const storageKey = useMemo(
        () => `ads_seen_${sessionKey}`,
        [sessionKey]
    );

    const currentAd = ads[activeIndex];

    const isCurrentImageLoaded =
        !currentAd?.image ||
        loadedImages[currentAd.id];

    /* ================= LOAD ADS ================= */
    useEffect(() => {

        if (!clubId) return;

        const q = query(
            collection(db, "clubs", clubId, "ads"),
            where("enabled", "==", true)
        );

        return onSnapshot(q, (snap) => {

            const now = Date.now();

            const activeAds = snap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter(ad => {

                    if (!ad.expiresAt) return true;

                    return ad.expiresAt.toMillis() > now;
                })
                .sort((a, b) => {

                    const orderA =
                        typeof a.order === "number"
                            ? a.order
                            : 9999;

                    const orderB =
                        typeof b.order === "number"
                            ? b.order
                            : 9999;

                    return orderA - orderB;
                });

            setAds(activeAds);

            if (activeAds.length === 0) return;

            const saved =
                localStorage.getItem(storageKey);

            let shouldShow = true;

            if (saved) {

                try {

                    const parsed = JSON.parse(saved);

                    //2 hours for now
                    const hoursToDisplayAgain =
                        1000 * 60 * 60 * 2;

                    if (
                        Date.now() - parsed.dismissedAt <
                        hoursToDisplayAgain
                    ) {
                        shouldShow = false;
                    }

                } catch {
                    shouldShow = true;
                }
            }

            if (shouldShow) {
                setShow(true);
                setActiveIndex(0);
            }

        });

    }, [clubId, storageKey]);

    /* ================= DISMISS ================= */
    const dismissAds = () => {

        localStorage.setItem(
            storageKey,
            JSON.stringify({
                dismissedAt: Date.now()
            })
        );

        setShow(false);
    };

    /* ================= NAVIGATION ================= */
    const nextSlide = () => {

        if (activeIndex >= ads.length - 1)
            return;

        setActiveIndex(prev => prev + 1);
    };

    const prevSlide = () => {

        if (activeIndex <= 0)
            return;

        setActiveIndex(prev => prev - 1);
    };

    /* ================= TOUCH ================= */
    const handleTouchStart = (e) => {
        touchStartX.current =
            e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {

        const endX =
            e.changedTouches[0].screenX;

        const diff =
            touchStartX.current - endX;

        if (diff > 50) {
            nextSlide();
        }

        if (diff < -50) {
            prevSlide();
        }
    };

    if (!show || ads.length === 0)
        return null;

    return (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
                zIndex: 30000,
                background: "rgba(0,0,0,0.72)",
                backdropFilter: "blur(8px)",
                padding: 16,
                pointerEvents: "auto"
            }}
        >

            {/* MODAL */}
            <div
                className="position-relative overflow-hidden rounded-4 shadow-lg d-flex flex-column"
                style={{
                    width: "100%",
                    maxWidth: 520,
                    maxHeight: "92vh",
                    background: "var(--card-bg)",
                    border: "1px solid var(--border-color)",
                    pointerEvents: "auto"
                }}
            >

                {/* CLOSE */}
                {activeIndex === ads.length - 1 && (
                    <button
                        className="btn btn-light rounded-circle position-absolute"
                        style={{
                            top: 12,
                            right: 12,
                            width: 38,
                            height: 38,
                            zIndex: 100
                        }}
                        onClick={dismissAds}
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                )}

                {/* BODY */}
                <div
                    className="overflow-hidden flex-grow-1"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >

                    {/* TRACK */}
                    <div
                        className="d-flex"
                        style={{
                            width: `${ads.length * 100}%`,
                            transform: `translateX(-${activeIndex * (100 / ads.length)}%)`,
                            transition: "transform 0.35s ease"
                        }}
                    >

                        {ads.map((ad) => (

                            <div
                                key={ad.id}
                                style={{
                                    width: `${100 / ads.length}%`,
                                    flexShrink: 0
                                }}
                            >

                                {/* IMAGE */}
                                {/* IMAGE */}
                                {ad.image && (
                                    <div
                                        className="position-relative d-flex align-items-center justify-content-center"
                                        style={{
                                            background: "#000",
                                            minHeight: 220,
                                            maxHeight: "70vh",
                                            overflow: "hidden"
                                        }}
                                    >

                                        {!loadedImages[ad.id] && (
                                            <div
                                                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                style={{
                                                    background: "rgba(0,0,0,0.15)",
                                                    zIndex: 2
                                                }}
                                            >
                                                <div
                                                    className="spinner-border"
                                                    style={{
                                                        color: themeColor
                                                    }}
                                                />
                                            </div>
                                        )}

                                        <img
                                            src={ad.image}
                                            alt={ad.title}
                                            draggable={false}
                                            onLoad={() =>
                                                setLoadedImages(prev => ({
                                                    ...prev,
                                                    [ad.id]: true
                                                }))
                                            }
                                            style={{
                                                width: "100%",
                                                maxHeight: "70vh",
                                                objectFit: "contain",
                                                userSelect: "none",
                                                opacity: loadedImages[ad.id] ? 1 : 0,
                                                transition: "opacity 0.25s ease",
                                                display: "block"
                                            }}
                                        />
                                    </div>
                                )}

                                {/* CONTENT */}
                                <div className="p-4">

                                    {/* COUNTER */}
                                    <div
                                        className="small fw-semibold mb-2"
                                        style={{
                                            color: themeColor
                                        }}
                                    >
                                        Ad {activeIndex + 1} of {ads.length}
                                    </div>

                                    {/* TITLE */}
                                    <div
                                        className="fw-bold mb-2"
                                        style={{
                                            fontSize: "1.3rem",
                                            color: "var(--text-color)"
                                        }}
                                    >
                                        {ad.title}
                                    </div>

                                    {/* DESCRIPTION */}
                                    <div
                                        className="mb-4"
                                        style={{
                                            color: "var(--muted-text)"
                                        }}
                                    >
                                        {ad.description}
                                    </div>

                                    {/* CTA */}
                                    {ad.buttonLink && (
                                        <a
                                            href={ad.buttonLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn w-100 text-white fw-bold rounded-pill py-3 mb-3"
                                            style={{
                                                background: themeColor
                                            }}
                                        >
                                            {ad.buttonText || "Learn More"}
                                        </a>
                                    )}

                                    {/* NAV */}
                                    {/* <div className="d-flex gap-2">

                                        <button
                                            type="button"
                                            className="btn btn-light border rounded-pill w-100"
                                            disabled={activeIndex === 0}
                                            onClick={prevSlide}
                                            style={{
                                                pointerEvents: "auto"
                                            }}
                                        >
                                            Previous
                                        </button>

                                        {activeIndex < ads.length - 1 ? (
                                            <button
                                                type="button"
                                                className="btn text-white rounded-pill w-100 fw-bold"
                                                style={{
                                                    background: themeColor,
                                                    pointerEvents: "auto"
                                                }}
                                                onClick={nextSlide}
                                                disabled={!isCurrentImageLoaded}
                                            >
                                                {!isCurrentImageLoaded
                                                    ? "Loading..."
                                                    : "Next"}
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn text-white rounded-pill w-100 fw-bold"
                                                disabled={!isCurrentImageLoaded}
                                                style={{
                                                    background: themeColor,
                                                    pointerEvents: "auto"
                                                }}
                                                onClick={dismissAds}
                                            >
                                                Continue
                                            </button>
                                        )}

                                    </div> */}

                                </div>

                            </div>
                        ))}

                    </div>

                </div>
                {/* FIXED BOTTOM ACTIONS */}
                <div
                    className="p-4 pt-2 border-top"
                    style={{
                        background: "var(--card-bg)",
                        position: "sticky",
                        bottom: 0,
                        zIndex: 20
                    }}
                >
                    <div className="d-flex gap-2">

                        <button
                            type="button"
                            className="btn btn-light border rounded-pill w-100"
                            disabled={activeIndex === 0}
                            onClick={prevSlide}
                        >
                            Previous
                        </button>

                        {activeIndex < ads.length - 1 ? (
                            <button
                                type="button"
                                className="btn text-white rounded-pill w-100 fw-bold"
                                style={{
                                    background: themeColor
                                }}
                                onClick={nextSlide}
                                disabled={!isCurrentImageLoaded}
                            >
                                {!isCurrentImageLoaded
                                    ? "Loading..."
                                    : "Next"}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn text-white rounded-pill w-100 fw-bold"
                                disabled={!isCurrentImageLoaded}
                                style={{
                                    background: themeColor
                                }}
                                onClick={dismissAds}
                            >
                                Continue
                            </button>
                        )}

                    </div>
                </div>
                {/* DOTS */}
                <div className="d-flex justify-content-center gap-2 pb-4">

                    {ads.map((_, index) => (

                        <button
                            key={index}
                            type="button"
                            onClick={() => setActiveIndex(index)}
                            style={{
                                width:
                                    activeIndex === index
                                        ? 24
                                        : 8,

                                height: 8,

                                borderRadius: 999,
                                border: "none",

                                background:
                                    activeIndex === index
                                        ? themeColor
                                        : "rgba(255,255,255,0.25)",

                                transition: "all 0.25s ease",
                                cursor: "pointer"
                            }}
                        />

                    ))}

                </div>

            </div>

        </div>
    );
}